/* eslint-disable max-len */
/* eslint-disable import/prefer-default-export */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as VMIcon } from 'assets/icons_3/server.svg';
import { ReactComponent as AsgIcon } from 'assets/icons_3/asg.svg';
import { ReactComponent as NetworkIcon } from 'assets/icons_3/network.svg';
import { ReactComponent as ReportIcon } from 'assets/icons_3/chart-2.svg';
import { ReactComponent as HDDIcon } from 'assets/icons_3/disk.svg';
import { ReactComponent as UserIcon, ReactComponent as UserSettingsIcon } from 'assets/icons_3/user.svg';
import { ReactComponent as BackupIcon } from 'assets/icons_3/backup.svg';
import { ReactComponent as LogIcon } from 'assets/icons_3/clipboard-check.svg';
import { ReactComponent as PasswordIcon } from 'assets/icons_3/passcode-lock.svg';
import { ReactComponent as TwoFAIcon } from 'assets/icons_3/lock-2.svg';
import { ReactComponent as ShieldIcon } from 'assets/icons_3/shield-tick.svg';
import { ReactComponent as BellIcon } from 'assets/icons_3/bell.svg';
import { ReactComponent as NotificationSettingsIcon } from 'assets/icons_3/notification-list.svg';
import { ReactComponent as StorageIcon } from 'assets/icons_3/box.svg';
import { ReactComponent as KeyIcon } from 'assets/icons_3/key.svg';
import { ReactComponent as KubernetesIcon } from 'assets/icons_3/kubernetes.svg';
import { ReactComponent as DatabaseIcon } from 'assets/icons_3/database-02.svg';
// import { ReactComponent as AlertIcon } from 'assets/icons/new-icons/alert.svg';
import { ReactComponent as LoadbalancerIcon } from 'assets/icons_3/loadbalancer.svg';
import { ReactComponent as ProjectIcon } from 'assets/icons_3/rocket.svg';
import { ReactComponent as ResourcesIcon } from 'assets/icons_3/dataflow.svg';
import { ReactComponent as TemplateIcon } from 'assets/icons_3/template.svg';
import { ReactComponent as HomeIcon } from 'assets/icons_3/home.svg';
import { ReactComponent as SettingsIcon } from 'assets/icons_3/settings.svg';
import { ReactComponent as BackIcon } from 'assets/icons_3/arrows/arrow-left.svg';
import { ReactComponent as AlertIcon } from 'assets/icons_3/alert-triangle2.svg';
import { ReactComponent as RouterIcon } from 'assets/icons_3/route.svg';
import { isServiceEnabledInProd } from 'components/SideMenu/utils';
import { User } from 'store/user/types';
import { ServiceCode } from 'store/user/types/service';
// import { ServicePermissions } from 'components/Project/types/enums';

export type NavLinkConfig = {
  path: string;
  title: string;
  Icon?: React.FunctionComponent<
  React.SVGProps<SVGSVGElement> & {
    title?: string | undefined;
  }
  >;
  className?: string;
  disabled?: boolean
  hidden?: boolean
};

export type NavLinks = Map<{ id: string, title?: string, hidden?: boolean }, Array<NavLinkConfig>>;
export const SETTINGS_LINK_KEY = {
  id: 'settings-link',
};
export const SETTINGS_BACK_KEY = {
  id: 'settings-back',
};

export function useCustomerLinks(user: User, isStorageActive: boolean, hideResources: boolean) {
  const [t] = useTranslation();

  const mainLinks: NavLinks = new Map();

  mainLinks.set({
    id: 'main-page',
  }, [
    {
      path: '/services/cloud-platform',
      title: t('navigation.main_page'),
      Icon: HomeIcon,
    },
  ]);

  mainLinks.set({
    id: 'services',
    title: t('navigation.services'),
    // hidden: !hasAnyServicePermission(user.currentProject!.permissions, [ServicePermissions.VIRTUAL_SERVERS,
    //   ServicePermissions.LOAD_BALANCERS,
    //   ServicePermissions.NETWORK,
    //   ServicePermissions.SECURITY_GROUPS,
    //   ServicePermissions.DISKS,
    //   ServicePermissions.BACKUPS,
    //   ServicePermissions.CUSTOM_IMAGES,
    //   ServicePermissions.ALERTS]),
  }, [
    {
      path: '/services/servers',
      title: t('vm.self_plural_long'),
      Icon: VMIcon,
      hidden: !user.currentProject?.permissions.VIRTUAL_SERVERS,
    },
    {
      path: '/services/autoscaling-groups',
      title: t('autoscaling_groups.self'),
      Icon: AsgIcon,
      hidden: !isServiceEnabledInProd(user, ServiceCode.asg) || !user.currentProject?.permissions.AUTO_SCALING_GROUPS,
    },
    {
      path: '/services/loadbalancers',
      title: t('loadbalancer.self'),
      Icon: LoadbalancerIcon,
      // beta: true,
      hidden: !isServiceEnabledInProd(user, ServiceCode.loadbalancer) || !user.currentProject?.permissions.LOAD_BALANCERS,
    },
    {
      path: '/services/networks',
      title: t('network.self_plural'),
      Icon: NetworkIcon,
      hidden: !user.currentProject?.permissions.NETWORK,
    },
    {
      path: '/services/routers',
      title: t('network.router.self_plural'),
      Icon: RouterIcon,
      hidden: !isServiceEnabledInProd(user, ServiceCode.routers) || !user.currentProject?.permissions.ROUTERS,
    },
    {
      path: '/services/security-groups',
      title: t('firewall.title'),
      Icon: ShieldIcon,
      hidden: !user.currentProject?.permissions.SECURITY_GROUPS,
    },
    {
      path: '/services/disks',
      title: t('disk.self_plural'),
      Icon: HDDIcon,
      hidden: !user.currentProject?.permissions.DISKS,
    },
    {
      path: '/services/backups',
      title: t('backup.self_plural'),
      Icon: BackupIcon,
      hidden: !user.currentProject?.permissions.BACKUPS,
    },
    {
      path: '/services/templates',
      title: t('os_template.title'),
      Icon: TemplateIcon,
      hidden: !user.currentProject?.permissions.CUSTOM_IMAGES,
    },
    {
      path: '/services/alerts',
      title: t('navigation.alerts'),
      Icon: AlertIcon,
      hidden: !isServiceEnabledInProd(user, ServiceCode.alerts) || !user.currentProject?.permissions.ALERTS,
    },
  ]);

  mainLinks.set({
    id: 'data-storing',
    title: t('navigation.data_storing'),
    // hidden: !hasAnyServicePermission(user.currentProject!.permissions, [ServicePermissions.S3]),
  }, [
    {
      path: '/services/databases',
      title: t('db.title'),
      Icon: DatabaseIcon,
      hidden: !isServiceEnabledInProd(user, ServiceCode.dbaas) || !user.currentProject?.permissions.MANAGED_DATABASES,
    },
    {
      path: '/services/storage',
      title: t('storage.self'),
      Icon: StorageIcon,
      hidden: !isServiceEnabledInProd(user, ServiceCode.storage) || !user.currentProject?.permissions.S3,
    },
  ]);

  mainLinks.set({
    id: 'containers',
    title: t('navigation.containers'),
    // hidden: !hasAnyServicePermission(user.currentProject!.permissions, [ServicePermissions.KUBERNETES]),
  }, [
    {
      path: '/services/clusters',
      title: t('kuber.clusters'),
      Icon: KubernetesIcon,
      // beta: true,
      hidden: !isServiceEnabledInProd(user, ServiceCode.k8s) || !user.currentProject?.permissions.KUBERNETES,
    },

  ]);

  mainLinks.set(SETTINGS_LINK_KEY, [
    {
      path: '/settings/account',
      title: t('navigation.settings'),
      Icon: SettingsIcon,
      hidden: true,
    },
  ]);

  const settingsLinks: NavLinks = new Map();

  settingsLinks.set(SETTINGS_BACK_KEY, [
    {
      path: '/services/cloud-platform',
      title: t('navigation.settings'),
      Icon: BackIcon,
      hidden: true,
    },
  ]);

  settingsLinks.set({
    id: 'settings',
    title: t('navigation.account'),
  }, [
    {
      path: '/settings/account',
      title: t('navigation.profile'),
      Icon: UserSettingsIcon,
    },
    {
      path: '/settings/notification-settings',
      title: t('app_notification.settings'),
      Icon: BellIcon,
    },
    {
      path: '/settings/notifications-list',
      title: t('app_notification.list'),
      Icon: NotificationSettingsIcon,
    },
    {
      path: '/settings/users',
      title: t('user.users_manage'),
      Icon: UserIcon,
      hidden: !user.isRootUser,
    },
    {
      path: '/settings/access-keys',
      title: t('access_key.self_plural'),
      Icon: KeyIcon,
    },

  ]);

  settingsLinks.set({
    id: 'security',
    title: t('navigation.security'),
  }, [
    {
      path: '/settings/change-pass',
      title: t('auth.change_pass'),
      Icon: PasswordIcon,
    },
    {
      path: '/settings/two-fa',
      title: t('two_fa.self'),
      Icon: TwoFAIcon,
    },

  ]);

  settingsLinks.set({
    id: 'projects',
    title: t('navigation.projects'),
  }, [

    {
      path: '/settings/projects',
      title: t('project.management'),
      Icon: ProjectIcon,
    },
    {
      path: '/settings/resources',
      title: t('resources.management'),
      Icon: ResourcesIcon,
      hidden: !user.isRootUser || hideResources,
    },

  ]);
  settingsLinks.set({
    id: 'statistics',
    title: t('navigation.statistics'),
  }, [
    {
      path: '/settings/log',
      title: t('log.self'),
      Icon: LogIcon,
    },
    {
      path: '/settings/reports',
      title: t('report.self_short'),
      Icon: ReportIcon,
    },
  ]);

  return {
    mainLinks, settingsLinks,
  };
}
